import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "./ImageUploader";
import MusicSettings from "./MusicSettings";
import { applyTypography } from "@/lib/settings";

const HEADING_FONTS = ["Fraunces", "Playfair Display", "Cormorant Garamond", "DM Serif Display", "Marcellus", "EB Garamond", "Libre Caslon Text", "Lora"];
const BODY_FONTS = ["Manrope", "Inter", "Jost", "Work Sans", "Nunito Sans", "Karla", "Lora", "Source Sans 3"];

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <div>Loading…</div>;

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setN = (k, sub, v) => setS((p) => ({ ...p, [k]: { ...(p[k] || {}), [sub]: v } }));
  const setTypo = (sub, v) => {
    const ty = { ...(s.typography || {}), [sub]: v };
    set("typography", ty);
    applyTypography(ty);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", s);
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const persistMusic = async (music) => {
    const next = { ...s, music };
    setS(next);
    try {
      await api.put("/admin/settings", next);
      toast.success("Music settings saved");
    } catch {
      toast.error("Auto-save failed — click Save to retry");
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Site</div>
          <h1 className="font-display text-4xl mt-3">Settings</h1>
        </div>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-sm rounded-md" data-testid="settings-save">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <Tabs defaultValue="brand" className="mt-8">
        <TabsList>
          <TabsTrigger value="brand" data-testid="settings-tab-brand">Brand</TabsTrigger>
          <TabsTrigger value="typography" data-testid="settings-tab-typography">Typography</TabsTrigger>
          <TabsTrigger value="footer" data-testid="settings-tab-footer">Footer</TabsTrigger>
          <TabsTrigger value="contact" data-testid="settings-tab-contact">Contact</TabsTrigger>
          <TabsTrigger value="floating" data-testid="settings-tab-floating">Floating CTAs</TabsTrigger>
          <TabsTrigger value="seo" data-testid="settings-tab-seo">SEO defaults</TabsTrigger>
          <TabsTrigger value="quotation" data-testid="settings-tab-quotation">Quotation PDF</TabsTrigger>
          <TabsTrigger value="music" data-testid="settings-tab-music">Ambient music</TabsTrigger>
        </TabsList>

        <TabsContent value="brand" className="space-y-4 mt-6 max-w-2xl">
          <Input value={s.site_name || ""} onChange={(e) => set("site_name", e.target.value)} placeholder="Site name" />
          <Input value={s.tagline || ""} onChange={(e) => set("tagline", e.target.value)} placeholder="Tagline" />
          <ImageUploader imageOnly label="Logo — for LIGHT backgrounds (dark text)" value={s.logo_url} onChange={(v) => set("logo_url", v)} testId="logo-uploader" />
          <ImageUploader imageOnly label="Logo — for DARK backgrounds (white/light text)" value={s.logo_url_dark} onChange={(v) => set("logo_url_dark", v)} testId="logo-dark-uploader" />
          <p className="text-xs text-zinc-500">The light-background logo is used on the scrolled header, light pages and the footer&apos;s ivory tile. The dark-background logo is used over hero sections and the admin login.</p>
          <ImageUploader imageOnly label="Favicon" value={s.favicon_url} onChange={(v) => set("favicon_url", v)} testId="favicon-uploader" />
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={async () => {
                try {
                  const { data } = await api.post("/admin/generate-icons");
                  setS((p) => ({ ...p, favicon_url: data.favicon, apple_icon_url: data.apple, seo_defaults: { ...(p.seo_defaults || {}), og_image: p.seo_defaults?.og_image || data.og } }));
                  toast.success("Favicon, Apple icon & OG image generated from your logo");
                } catch (e) {
                  toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Icon generation failed");
                }
              }}
              className="text-xs px-3 py-2 border border-zinc-300 rounded-md hover:bg-zinc-50"
              data-testid="generate-icons-btn"
            >
              ✨ Auto-generate favicon, Apple icon & OG image from logo
            </button>
          </div>
          <p className="text-xs text-zinc-500">Generates a browser-tab favicon, an apple-touch-icon (home-screen bookmark) and a social share image from your uploaded logo. The OG image is only set if you haven&apos;t uploaded one already.</p>

          <div className="mt-8 pt-6 border-t border-zinc-200 space-y-4">
            <div className="text-xs font-medium uppercase tracking-wide text-zinc-500">Site hygiene</div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500">Browser tab title</label>
              <Input
                value={s.seo_defaults?.title || ""}
                onChange={(e) => setN("seo_defaults", "title", e.target.value)}
                placeholder="e.g. Happy Retreats — Luxury Celebration Getaways near Bangalore"
                data-testid="field-browser-title"
                className="mt-1.5"
              />
              <p className="text-xs text-zinc-500 mt-1.5">Shown in the browser tab and as the default page title across the site. Also used as the default meta title for SEO.</p>
            </div>
            <div className="flex items-center justify-between bg-zinc-50 border border-zinc-200 rounded-md px-4 py-3">
              <div>
                <div className="text-sm font-medium">Show &ldquo;Made with Emergent&rdquo; badge</div>
                <div className="text-xs text-zinc-500 mt-0.5">Off by default. Turn on only if you want to display the Emergent attribution badge on your live site.</div>
              </div>
              <Switch
                checked={!!s.show_emergent_badge}
                onCheckedChange={(v) => set("show_emergent_badge", v)}
                data-testid="toggle-emergent-badge"
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="typography" className="mt-6 max-w-2xl space-y-5">
          <p className="text-xs text-zinc-500">Changes preview instantly in this dashboard. Hit Save to apply them to the public site. Leave a field on its default to keep the current design.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500">Heading font</label>
              <Select value={s.typography?.heading_font || "Fraunces"} onValueChange={(v) => setTypo("heading_font", v)}>
                <SelectTrigger className="mt-1.5" data-testid="typo-heading-font"><SelectValue /></SelectTrigger>
                <SelectContent>{HEADING_FONTS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500">Body font</label>
              <Select value={s.typography?.body_font || "Manrope"} onValueChange={(v) => setTypo("body_font", v)}>
                <SelectTrigger className="mt-1.5" data-testid="typo-body-font"><SelectValue /></SelectTrigger>
                <SelectContent>{BODY_FONTS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500">Body text size</label>
              <Select value={String(s.typography?.body_size || 16)} onValueChange={(v) => setTypo("body_size", Number(v))}>
                <SelectTrigger className="mt-1.5" data-testid="typo-body-size"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[14, 15, 16, 17, 18, 19, 20].map((n) => <SelectItem key={n} value={String(n)}>{n}px{n === 16 ? " (default)" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500">Line spacing (within a paragraph)</label>
              <Select value={String(s.typography?.line_height || 1.75)} onValueChange={(v) => setTypo("line_height", Number(v))}>
                <SelectTrigger className="mt-1.5" data-testid="typo-line-height"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1.5">Compact (1.5)</SelectItem>
                  <SelectItem value="1.6">Snug (1.6)</SelectItem>
                  <SelectItem value="1.75">Normal (1.75 — default)</SelectItem>
                  <SelectItem value="1.9">Relaxed (1.9)</SelectItem>
                  <SelectItem value="2.1">Airy (2.1)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500">Paragraph spacing (gap between paragraphs)</label>
              <Select value={String(s.typography?.paragraph_gap || 1.75)} onValueChange={(v) => setTypo("paragraph_gap", Number(v))}>
                <SelectTrigger className="mt-1.5" data-testid="typo-para-gap"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Compact</SelectItem>
                  <SelectItem value="1.5">Cozy</SelectItem>
                  <SelectItem value="1.75">Normal (default)</SelectItem>
                  <SelectItem value="2.25">Relaxed</SelectItem>
                  <SelectItem value="3">Airy</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500">Heading letter-spacing</label>
              <Select value={String(s.typography?.heading_tracking ?? -0.025)} onValueChange={(v) => setTypo("heading_tracking", Number(v))}>
                <SelectTrigger className="mt-1.5" data-testid="typo-heading-tracking"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="-0.05">Very tight (-0.05em)</SelectItem>
                  <SelectItem value="-0.025">Tight (default)</SelectItem>
                  <SelectItem value="0">Normal (0)</SelectItem>
                  <SelectItem value="0.02">Open (+0.02em)</SelectItem>
                  <SelectItem value="0.05">Wide (+0.05em)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500">Heading weight</label>
              <Select value={String(s.typography?.heading_weight || 300)} onValueChange={(v) => setTypo("heading_weight", Number(v))}>
                <SelectTrigger className="mt-1.5" data-testid="typo-heading-weight"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="300">Light (default)</SelectItem>
                  <SelectItem value="400">Regular</SelectItem>
                  <SelectItem value="500">Medium</SelectItem>
                  <SelectItem value="600">Semi-bold</SelectItem>
                  <SelectItem value="700">Bold</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <button
            type="button"
            onClick={() => { set("typography", {}); applyTypography({}); }}
            className="text-xs px-3 py-2 border border-zinc-300 rounded-md hover:bg-zinc-50"
            data-testid="typo-reset-defaults"
          >
            Reset typography to defaults
          </button>
          <div className="bg-zinc-50 border border-zinc-200 rounded-md p-6" data-testid="typo-preview">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-4">Preview</div>
            <div className="font-display text-4xl" style={{ lineHeight: 1.05 }}>
              Celebrations deserve a brand they can trust.
              <em className="accent-block text-clay italic">India's first celebrations-led hospitality.</em>
            </div>
            <div className="para-stack mt-6 font-sans font-light text-zinc-600" style={{ fontSize: "var(--hr-body-size)", lineHeight: "var(--hr-line-height)" }}>
              <p>Most hospitality brands were built to sell rooms. Celebrations simply became one of the many experiences they offered.</p>
              <p>In 2016, we saw an opportunity to do something different — a hospitality brand where celebrations come first.</p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="footer" className="mt-6 space-y-6 max-w-3xl">
          <Textarea value={s.footer?.tagline || ""} onChange={(e) => setN("footer", "tagline", e.target.value)} placeholder="Footer tagline" rows={3} />
          {(s.footer?.columns || []).map((col, ci) => (
            <div key={ci} className="bg-zinc-50 border border-zinc-200 rounded-md p-4">
              <Input value={col.title} onChange={(e) => { const cols = [...s.footer.columns]; cols[ci] = { ...cols[ci], title: e.target.value }; setN("footer", "columns", cols); }} placeholder="Column title" />
              <div className="mt-3 space-y-2">
                {(col.links || []).map((l, li) => (
                  <div key={li} className="flex gap-2">
                    <Input value={l.label} onChange={(e) => { const cols = [...s.footer.columns]; const links = [...cols[ci].links]; links[li] = { ...links[li], label: e.target.value }; cols[ci] = { ...cols[ci], links }; setN("footer", "columns", cols); }} placeholder="Label" />
                    <Input value={l.url} onChange={(e) => { const cols = [...s.footer.columns]; const links = [...cols[ci].links]; links[li] = { ...links[li], url: e.target.value }; cols[ci] = { ...cols[ci], links }; setN("footer", "columns", cols); }} placeholder="URL" />
                    <button onClick={() => { const cols = [...s.footer.columns]; cols[ci] = { ...cols[ci], links: cols[ci].links.filter((_, x) => x !== li) }; setN("footer", "columns", cols); }} className="px-3 text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                <button onClick={() => { const cols = [...s.footer.columns]; cols[ci] = { ...cols[ci], links: [...(cols[ci].links || []), { label: "", url: "" }] }; setN("footer", "columns", cols); }} className="text-xs inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add link</button>
              </div>
            </div>
          ))}
          <button onClick={() => setN("footer", "columns", [...(s.footer?.columns || []), { title: "New column", links: [] }])} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 border rounded-md"><Plus className="w-3 h-3" /> Add column</button>
          <Input value={s.footer?.copyright || ""} onChange={(e) => setN("footer", "copyright", e.target.value)} placeholder="Copyright text" />

          <div className="text-sm font-medium mt-6">Social links</div>
          {(s.footer?.social_links || []).map((sl, i) => (
            <div key={i} className="flex gap-2">
              <Input value={sl.platform} onChange={(e) => { const arr = [...s.footer.social_links]; arr[i] = { ...arr[i], platform: e.target.value }; setN("footer", "social_links", arr); }} placeholder="Platform (Instagram/YouTube)" />
              <Input value={sl.url} onChange={(e) => { const arr = [...s.footer.social_links]; arr[i] = { ...arr[i], url: e.target.value }; setN("footer", "social_links", arr); }} placeholder="URL" />
              <button onClick={() => setN("footer", "social_links", s.footer.social_links.filter((_, x) => x !== i))} className="px-3 text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={() => setN("footer", "social_links", [...(s.footer?.social_links || []), { platform: "", url: "" }])} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 border rounded-md"><Plus className="w-3 h-3" /> Add social</button>
        </TabsContent>

        <TabsContent value="contact" className="mt-6 max-w-2xl space-y-4">
          <Input value={s.contact?.phone || ""} onChange={(e) => setN("contact", "phone", e.target.value)} placeholder="Phone" />
          <Input value={s.contact?.whatsapp_number || ""} onChange={(e) => setN("contact", "whatsapp_number", e.target.value)} placeholder="WhatsApp number (digits only, with country code)" />
          <Input value={s.contact?.email || ""} onChange={(e) => setN("contact", "email", e.target.value)} placeholder="Email" />
          <Input value={s.contact?.address || ""} onChange={(e) => setN("contact", "address", e.target.value)} placeholder="Address" />
          <Input value={s.contact?.map_url || ""} onChange={(e) => setN("contact", "map_url", e.target.value)} placeholder="Google Maps URL" />
        </TabsContent>

        <TabsContent value="floating" className="mt-6 max-w-2xl space-y-6">
          <div>
            <div className="text-sm font-medium">Floating (sticky) CTA buttons</div>
            <p className="text-xs text-zinc-500 mt-1">These are the three buttons anchored to the bottom-right on every page: <strong>Enquire now</strong>, <strong>Phone call</strong> and <strong>WhatsApp</strong>. Toggle each one independently for mobile and desktop.</p>
          </div>

          {[
            { key: "enquire", label: "Enquire now button", hint: "Opens the enquiry form drawer." },
            { key: "phone", label: "Phone call button", hint: "Dials the phone number from the Contact tab." },
            { key: "whatsapp", label: "WhatsApp button", hint: "Opens WhatsApp chat with the number from the Contact tab." },
          ].map((row) => {
            const mKey = `${row.key}_mobile`;
            const dKey = `${row.key}_desktop`;
            const mVal = s.sticky_cta?.[mKey] !== false;
            const dVal = s.sticky_cta?.[dKey] !== false;
            return (
              <div key={row.key} className="bg-zinc-50 border border-zinc-200 rounded-md p-4">
                <div className="text-sm font-medium">{row.label}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{row.hint}</div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <label className="flex items-center justify-between bg-white border border-zinc-200 rounded-md px-3 py-2">
                    <span className="text-xs uppercase tracking-wider text-zinc-600">Mobile</span>
                    <Switch
                      checked={mVal}
                      onCheckedChange={(v) => setN("sticky_cta", mKey, v)}
                      data-testid={`toggle-sticky-${row.key}-mobile`}
                    />
                  </label>
                  <label className="flex items-center justify-between bg-white border border-zinc-200 rounded-md px-3 py-2">
                    <span className="text-xs uppercase tracking-wider text-zinc-600">Desktop</span>
                    <Switch
                      checked={dVal}
                      onCheckedChange={(v) => setN("sticky_cta", dKey, v)}
                      data-testid={`toggle-sticky-${row.key}-desktop`}
                    />
                  </label>
                </div>
              </div>
            );
          })}

          <p className="text-xs text-zinc-500">Tip: on mobile, showing all three floating buttons can feel busy. Many teams keep only <strong>WhatsApp</strong> visible on mobile and show <strong>Enquire now</strong> + <strong>Phone</strong> on desktop only.</p>
        </TabsContent>

        <TabsContent value="seo" className="mt-6 max-w-2xl space-y-4">
          <Input value={s.seo_defaults?.title || ""} onChange={(e) => setN("seo_defaults", "title", e.target.value)} placeholder="Default meta title" />
          <Textarea value={s.seo_defaults?.description || ""} onChange={(e) => setN("seo_defaults", "description", e.target.value)} placeholder="Default meta description" rows={3} />
          <Input value={s.seo_defaults?.keywords || ""} onChange={(e) => setN("seo_defaults", "keywords", e.target.value)} placeholder="Default keywords" />
          <ImageUploader imageOnly label="Default OG image" value={s.seo_defaults?.og_image} onChange={(v) => setN("seo_defaults", "og_image", v)} testId="og-default" />
        </TabsContent>

        <TabsContent value="quotation" className="mt-6 max-w-2xl space-y-6">
          <p className="text-sm text-zinc-600">
            Customise the estimate/quotation PDF that guests download from the budget calculator. Leave a field blank to use the default.
          </p>

          <div className="space-y-3">
            <div className="text-xs font-medium uppercase tracking-wide text-zinc-500">Contact shown on the PDF</div>
            <Input value={s.estimate_pdf?.phone || ""} onChange={(e) => setN("estimate_pdf", "phone", e.target.value)} placeholder={`Phone (default: ${s.contact?.phone || "from Contact tab"})`} data-testid="pdf-phone" />
            <Input value={s.estimate_pdf?.email || ""} onChange={(e) => setN("estimate_pdf", "email", e.target.value)} placeholder={`Email (default: ${s.contact?.email || "from Contact tab"})`} data-testid="pdf-email" />
          </div>

          <div className="space-y-3">
            <div className="text-xs font-medium uppercase tracking-wide text-zinc-500">Bank / account details</div>
            <p className="text-xs text-zinc-500 -mt-1">Shown as a &ldquo;Payment details&rdquo; section on the PDF. Leave all blank to hide the section.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input value={s.estimate_pdf?.account_name || ""} onChange={(e) => setN("estimate_pdf", "account_name", e.target.value)} placeholder="Account name" data-testid="pdf-account-name" />
              <Input value={s.estimate_pdf?.bank_name || ""} onChange={(e) => setN("estimate_pdf", "bank_name", e.target.value)} placeholder="Bank name" data-testid="pdf-bank-name" />
              <Input value={s.estimate_pdf?.account_number || ""} onChange={(e) => setN("estimate_pdf", "account_number", e.target.value)} placeholder="Account number" data-testid="pdf-account-number" />
              <Input value={s.estimate_pdf?.ifsc || ""} onChange={(e) => setN("estimate_pdf", "ifsc", e.target.value)} placeholder="IFSC code" data-testid="pdf-ifsc" />
              <Input value={s.estimate_pdf?.branch || ""} onChange={(e) => setN("estimate_pdf", "branch", e.target.value)} placeholder="Branch" data-testid="pdf-branch" />
              <Input value={s.estimate_pdf?.upi_id || ""} onChange={(e) => setN("estimate_pdf", "upi_id", e.target.value)} placeholder="UPI ID" data-testid="pdf-upi" />
              <Input value={s.estimate_pdf?.gstin || ""} onChange={(e) => setN("estimate_pdf", "gstin", e.target.value)} placeholder="GSTIN" data-testid="pdf-gstin" />
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-xs font-medium uppercase tracking-wide text-zinc-500">Terms &amp; conditions</div>
            <p className="text-xs text-zinc-500">One term per line. Leave blank to use the standard five terms.</p>
            <Textarea
              value={s.estimate_pdf?.terms || ""}
              onChange={(e) => setN("estimate_pdf", "terms", e.target.value)}
              rows={7}
              placeholder={"This estimate is indicative and based on the selections made.\nAll prices are inclusive of GST unless stated otherwise.\nDiscounts are applied as per the selected payment mode and exclude extra accommodation charges.\nPrices may change; final pricing is confirmed on booking.\nA booking is confirmed only upon receipt of the required advance and written confirmation from Happy Retreats."}
              data-testid="pdf-terms"
            />
          </div>
        </TabsContent>

        <TabsContent value="music" className="mt-6 max-w-2xl">
          <MusicSettings value={s.music} onChange={(v) => set("music", v)} onPersist={persistMusic} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
